import { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import type { TaskWithStats } from '../../services/api/tasks';
import { useProjectSlaTiers } from '../../hooks/useProjectSlaTiers';
import { SLA_TASK_TYPE_ID } from '../../constants/typeIds';
import { parseDecimalInput } from '../../utils/parseDecimalInput';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Phase 49 Plan 49-05 — RC-01: tierId included only when SLA project. */
  onSave: (data: {
    name: string;
    code?: string;
    status?: string;
    budget?: number | null;
    hardLimit?: boolean;
    plannedHours?: number | null;
    tierId?: number | null;
  }) => void;
  task?: TaskWithStats;
  projectId: number;
  /** Phase 49 Plan 49-05 — gates the tier select field. Drilled from TaskManagementPage. */
  projectTaskTypeId?: number | null;
  isLoading?: boolean;
  error?: string | null;
}

export function TaskModal({
  isOpen,
  onClose,
  onSave,
  task,
  projectId,
  projectTaskTypeId,
  isLoading,
  error,
}: TaskModalProps) {
  const isEdit = !!task;

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('');
  const [budget, setBudget] = useState('');
  const [hardLimit, setHardLimit] = useState(false);
  const [plannedHours, setPlannedHours] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  // Phase 49 Plan 49-05 — RC-01 tier state
  const [tierId, setTierId] = useState<number | null>(null);

  const isSlaProject = projectTaskTypeId === SLA_TASK_TYPE_ID;
  const tiersQuery = useProjectSlaTiers(isSlaProject && projectId ? projectId : undefined);
  const sortedTiers = (tiersQuery.data?.data ?? []).slice().sort((a, b) => a.orderIndex - b.orderIndex);

  useEffect(() => {
    if (isOpen) {
      setName(task?.name ?? '');
      setCode(task?.code ?? '');
      setStatus(task?.status ?? '');
      setBudget(task?.budget != null ? String(task.budget) : '');
      setHardLimit(task?.hardLimit ?? false);
      setPlannedHours(task?.plannedHours != null ? String(task.plannedHours) : '');
      setCodeTouched(false);
      // Phase 49 Plan 49-05 — TaskWithStats doesn't carry tierId; default null on open.
      setTierId(null);
    }
  }, [isOpen, task]);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!isEdit && !codeTouched) {
      setCode(value);
    }
  };

  const handleCodeChange = (value: string) => {
    setCodeTouched(true);
    setCode(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    // Phase 49 Plan 49-05 — RC-01 gate: block submit when SLA + no tier on create
    if (isSlaProject && !isEdit && tierId == null) return;

    const budgetValue = parseDecimalInput(budget);
    const plannedHoursValue = parseDecimalInput(plannedHours);

    onSave({
      name: name.trim(),
      ...(isEdit ? {} : { code: code.trim() || undefined }),
      status: status.trim() || undefined,
      budget: budgetValue,
      hardLimit: budgetValue != null ? hardLimit : undefined,
      plannedHours: plannedHoursValue,
      // Include tierId only on create for SLA projects
      ...(!isEdit && isSlaProject ? { tierId } : {}),
    });
  };

  const canSave =
    !!name.trim() &&
    !isLoading &&
    // Phase 49 Plan 49-05 — RC-01: require tier on create for SLA projects
    (!isSlaProject || isEdit || tierId != null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Task' : 'New Task'}
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            disabled={!canSave}
          >
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="task-modal-name" className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="task-modal-name"
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Task name"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm px-3 py-2.5"
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="task-modal-code" className="block text-sm font-medium text-gray-700 mb-1">
            Code
          </label>
          <input
            id="task-modal-code"
            type="text"
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            placeholder="Task code"
            disabled={isEdit}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm px-3 py-2.5 disabled:bg-gray-100 disabled:text-gray-500"
          />
          {isEdit && (
            <p className="mt-1 text-xs text-gray-400">Code cannot be changed after creation</p>
          )}
        </div>

        <div>
          <label htmlFor="task-modal-status" className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <input
            id="task-modal-status"
            type="text"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            placeholder="e.g. active, archived"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm px-3 py-2.5"
          />
        </div>

        <div>
          <label htmlFor="task-modal-budget" className="block text-sm font-medium text-gray-700 mb-1">
            Budget (CZK)
          </label>
          <input
            id="task-modal-budget"
            type="number"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="No budget limit"
            min="0"
            step="any"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm px-3 py-2.5"
          />
          <p className="mt-1 text-xs text-gray-400">Leave empty for no budget limit</p>
        </div>

        {budget.trim() && (
          <div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={hardLimit}
                onChange={(e) => setHardLimit(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="font-medium">Hard limit</span>
            </label>
            <p className="mt-1 text-xs text-gray-400 ml-6">
              {hardLimit
                ? 'Time entries will be blocked when budget is exceeded'
                : 'Users will see a warning but can still log time over budget'}
            </p>
          </div>
        )}

        <div>
          <label htmlFor="task-modal-planned-hours" className="block text-sm font-medium text-gray-700 mb-1">
            Planned hours
          </label>
          <input
            id="task-modal-planned-hours"
            type="number"
            value={plannedHours}
            onChange={(e) => setPlannedHours(e.target.value)}
            placeholder="No planned hours"
            min="0"
            step="any"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm px-3 py-2.5"
          />
          <p className="mt-1 text-xs text-gray-400">Leave empty for no planned hours tracking</p>
        </div>

        {/* Phase 49 Plan 49-05 — RC-01 tier select (SLA projects, create only) */}
        {isSlaProject && !isEdit && (
          <div>
            <label
              htmlFor="task-modal-tier"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Priority tier <span className="text-red-500">*</span>
            </label>
            <select
              id="task-modal-tier"
              value={tierId ?? ''}
              onChange={(e) => setTierId(e.target.value ? Number(e.target.value) : null)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm px-3 py-2.5"
              aria-label="Priority tier"
              data-testid="task-modal-tier-select"
            >
              <option value="">— select tier —</option>
              {sortedTiers.map((t) => (
                <option key={t.id} value={t.id}>
                  P{t.orderIndex} — {t.name}
                </option>
              ))}
            </select>
            {tierId == null && (
              <p className="mt-1 text-xs text-red-500">Required for SLA Task projects</p>
            )}
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
      </form>
    </Modal>
  );
}
